import { useEffect, useState } from "react";
import { Wand2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BADGE = {
  pending: "bg-amber-100 text-amber-700", quoted: "bg-violet-100 text-violet-700",
  changes_requested: "bg-orange-100 text-orange-700", rejected: "bg-rose-100 text-rose-700",
  confirmed: "bg-emerald-100 text-emerald-700", payment_initiated: "bg-sky-100 text-sky-700",
};

export default function SellerCustomRequests() {
  const [rows, setRows] = useState([]);
  const [quote, setQuote] = useState({});
  const load = () => api.get("/marketplace/custom/seller-requests").then((r) => setRows(r.data.requests || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (r, path, body) => {
    try { await api.post(`/marketplace/custom/${r.request_id}/${path}`, body || {}); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="seller-custom-requests">
      <PageHeader title="Customization Requests" icon={Wand2} subtitle="Review custom/made-to-order requests, send a quote, and get paid — the buyer only pays after you confirm the final price." />
      {rows.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="cr-empty">No customization requests yet.</p> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.request_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`cr-row-${r.request_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><b className="text-sm">{r.number}</b> <span className="text-xs text-slate-400">· {r.product_name} · {r.customer_name || r.customer_email}</span></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${BADGE[r.status] || "bg-slate-100"}`} data-testid={`cr-status-${r.request_id}`}>{r.status.replace(/_/g, " ")}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{r.requirements}</p>
              {r.files?.length > 0 && <div className="mt-1 flex flex-wrap gap-2 text-xs">{r.files.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noreferrer noopener noreferrer" className="text-sky-600 underline">{f.name || `File ${i + 1}`}</a>)}</div>}
              {r.quote_amount && <p className="mt-1 text-xs text-slate-500">Quoted: <b>₹{r.quote_amount}</b>{r.payment_status === "paid" ? " · Paid ✓" : ""}</p>}
              {["pending", "quoted", "changes_requested"].includes(r.status) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input type="number" value={quote[r.request_id] || ""} onChange={(e) => setQuote((q) => ({ ...q, [r.request_id]: e.target.value }))} placeholder="Quote ₹" className="h-8 w-28" data-testid={`cr-quote-input-${r.request_id}`} />
                  <button onClick={() => { const a = Number(quote[r.request_id]); if (!a) { toast.error("Enter a quote amount"); return; } act(r, "quote", { amount: a }); }} data-testid={`cr-quote-${r.request_id}`} className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">Send quote</button>
                  <button onClick={() => act(r, "changes", { note: "Please share more details / a clearer reference." })} data-testid={`cr-changes-${r.request_id}`} className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Request changes</button>
                  <button onClick={() => act(r, "reject")} data-testid={`cr-reject-${r.request_id}`} className="rounded-full border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
